import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../../../services/axiosInstance";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Pagination,
  TablePagination,
} from "@mui/material";
import apiEndPointsConfig from "../../../../../../services/apiEndPoints";

const ActivityTable = () => {
  const { id } = useParams();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const fetchActivity = async () => {
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllActivityDetails}/${id}`
      );
      setLogs(res.data.callLogs || []);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchActivity();
  }, [id]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <CircularProgress />
      </Box>
    );

  // Pagination Logic
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedLogs = logs.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box
      sx={{
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#4b5563" }}
        >
          Call Activity
        </Typography>

        {logs.length > 0 && (
          <Typography variant="caption" sx={{ color: "#9ca3af" }}>
            {logs.length} call{logs.length > 1 ? "s" : ""} recorded
          </Typography>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {/* No Data */}
      {!loading && logs.length === 0 && (
        <Box
          sx={{
            borderRadius: 2,
            border: "1px dashed #e5e7eb",
            bgcolor: "#f9fafb",
            p: 2.5,
            textAlign: "center",
            mx: 2.5,
            mb: 2.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#6b7280", fontStyle: "italic" }}
          >
            No call activity found for this deal.
          </Typography>
        </Box>
      )}

      {/* Table */}
      {!loading && logs.length > 0 && (
        <Box
          sx={{
            border: "1px solid #ebebeb",
            overflow: "hidden",
            bgcolor: "#fff",
            borderRadius: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "#fafafa",
                  "& th": {
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#374151",
                    py: 1.25,
                  },
                }}
              >
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Disposition</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Recording</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedLogs.map((log, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                    "&:hover": { bgcolor: "#f3f4f6" },
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>
                    {log.payload.callDate}
                  </TableCell>

                  <TableCell sx={{ fontSize: 13 }}>
                    {log.payload.calls_time}
                  </TableCell>

                  <TableCell sx={{ fontSize: 13 }}>
                    <Chip
                      label={log.payload.calls_direction}
                      size="small"
                      sx={{
                        backgroundColor:
                          log.payload.calls_direction === "Outbound"
                            ? "#e0f2fe"
                            : "#e8f5e9",
                        color:
                          log.payload.calls_direction === "Outbound"
                            ? "#0369a1"
                            : "#166534",
                        fontWeight: 600,
                        height: 20,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: 13 }}>
                    <Chip
                      label={log.payload.sub_disposition || "—"}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 11,
                        height: 20,
                        borderColor: "#e5e7eb",
                        color: "#6b7280",
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: 13 }}>
                    {log.payload.timeDuration} sec
                  </TableCell>

                  <TableCell sx={{ fontSize: 13 }}>
                    {log.payload.location !== "failed" ? (
                      <audio
                        controls
                        src={log.payload.location}
                        style={{
                          width: "150px",
                          height: "26px",
                        }}
                      />
                    ) : (
                      <Typography color="error" fontSize="12px">
                        No Recording
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Box>
            <TablePagination
              component="div"
              count={logs.length}
              page={page - 1}
              onPageChange={(e, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={() => { }}
              rowsPerPageOptions={[]}
              sx={{
                borderTop: "1px solid #e5e7eb",
                ".MuiTablePagination-toolbar": {
                  minHeight: "38px",
                  height: "38px",
                },
                ".MuiTablePagination-displayedRows": {
                  fontSize: "13px",
                  color: "#6b7280",
                },
                ".MuiIconButton-root": {
                  padding: "4px",
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ActivityTable;
